import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr, apiGet } from "@/lib/api";
import { fmtEUR, fmtDate, fmtHours } from "@/lib/format";
import { fmtDuration } from "@/lib/agenda";
import { QuickActions } from "@/components/QuickActions";
import { InstallBanner } from "@/components/pwa/InstallBanner";
import { UpcomingAppointments } from "@/components/UpcomingAppointments";
import { CollapsibleSection } from "@/components/CollapsibleSection";
import { DataUnavailable } from "@/components/pwa/OfflineBanner";
import { useResource } from "@/pwa/useResource";
import { StatusBadge } from "@/components/StatusBadge";
import { CompleteDialog } from "@/components/dialogs/CompleteDialog";
import { ReceivablesCard } from "@/components/ReceivablesCard";
import { ContactBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Wallet, Clock, Activity, Users, TrendingUp, CheckCircle2, PackageX, AlertCircle, CalendarClock, AlertTriangle, XCircle, UserCog, Phone } from "lucide-react";

const PERIODS = [{ v: "month", l: "Mese" }, { v: "year", l: "Anno" }, { v: "total", l: "Totale" }];

const Kpi = ({ icon: Icon, label, value, testid, accent }) => (
  <div className="rounded-2xl border border-border bg-card p-4" data-testid={testid}>
    <div className="flex items-center gap-2 text-muted-foreground text-xs font-semibold uppercase tracking-wide">
      <Icon className={`h-4 w-4 ${accent || ""}`} /> {label}
    </div>
    <div className="mt-2 font-head font-extrabold text-2xl tabular">{value}</div>
  </div>
);

export default function Dashboard() {
  const [period, setPeriod] = useState("month");
  const [complete, setComplete] = useState(null);
  const [allOpen, setAllOpen] = useState(() => {
    const ids = ["dash-upcoming", "dash-verificare", "dash-contatti", "dash-oggi", "dash-controllare", "dash-workflow"];
    try {
      const vals = ids.map((id) => localStorage.getItem(`k9-collapse:${id}`)).filter((v) => v !== null);
      return vals.length === 0 ? true : vals.some((v) => v === "1");
    } catch { return true; }
  });
  const nav = useNavigate();
  const toggleAll = () => { const n = !allOpen; setAllOpen(n); window.dispatchEvent(new CustomEvent("k9-collapse-all", { detail: { open: n } })); };

  const { data, loaded, unavailable, reload: load } = useResource(
    useCallback(async () => { const r = await apiGet("/dashboard", { params: { period } }); return { data: r.data, stale: r.stale }; }, [period]),
    [period]
  );
  const ready = loaded && !unavailable;

  const cancelSession = async (s) => {
    try { await api.patch(`/sessions/${s.id}/status`, { status: "Annullata" }); toast.success("Prestazione annullata"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const k = data?.kpi || {};
  const att = data?.attention || { packages: [], finance: [], workflow: [] };
  const dv = data?.da_verificare || [];
  const contatti = data?.contatti || [];

  const setStage = async (owner_id, stage) => {
    try { await api.patch(`/owners/${owner_id}/contact-stage`, { contact_stage: stage }); toast.success("Stato contatto aggiornato"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-head text-3xl font-extrabold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground text-sm">Cosa devo sapere o fare oggi.</p>
        </div>
        <div className="flex gap-1 rounded-full border border-border bg-card p-1">
          {PERIODS.map((p) => (
            <button key={p.v} data-testid={`period-${p.v}`} onClick={() => setPeriod(p.v)}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${period === p.v ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
              {p.l}
            </button>
          ))}
        </div>
      </div>

      <QuickActions onChange={load} />

      <InstallBanner />

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <Kpi icon={Wallet} label="Incassato" value={ready ? fmtEUR(k.incassato) : "—"} testid="kpi-incassato" accent="text-primary" />
        <Kpi icon={TrendingUp} label="Da incassare" value={ready ? fmtEUR(k.da_incassare) : "—"} testid="kpi-da-incassare" accent="text-accent" />
        <Kpi icon={Activity} label="Prestazioni" value={ready ? (k.prestazioni ?? 0) : "—"} testid="kpi-prestazioni" />
        <Kpi icon={Users} label="Cani attivi" value={ready ? (k.cani_attivi ?? 0) : "—"} testid="kpi-cani-attivi" />
        <Kpi icon={Clock} label="Ore erogate" value={ready ? fmtHours(k.ore_erogate) : "—"} testid="kpi-ore" />
        <Kpi icon={TrendingUp} label="Valore servizi/pacchetti" value={ready ? fmtEUR(k.valore) : "—"} testid="kpi-valore" />
      </div>

      <ReceivablesCard onNavigate={nav} />

      {unavailable && <DataUnavailable />}

      <div className="flex justify-end -mb-2">
        <button type="button" data-testid="dash-collapse-all" onClick={toggleAll}
          className="text-sm font-semibold text-muted-foreground hover:text-foreground">
          {allOpen ? "Comprimi tutto" : "Espandi tutto"}
        </button>
      </div>

      <UpcomingAppointments />

      {/* Da verificare */}
      {dv.length > 0 && (
        <CollapsibleSection id="dash-verificare" testid="da-verificare-section" title="Da verificare" icon={AlertTriangle} iconClass="text-[#C2410C]"
          badge={<span data-testid="da-verificare-count" className="text-sm font-bold px-2 py-0.5 rounded-full bg-[#FFF3E9] text-[#C2410C] border border-[#F5C6A5]">{dv.length}</span>}>
          <div className="space-y-2" data-testid="da-verificare-list">
            {dv.map((s) => (
              <div key={s.id} className="rounded-2xl border border-[#F5C6A5] bg-[#FFF8F2] p-4">
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <button onClick={() => s.dog_ids?.[0] && nav(`/cani/${s.dog_ids[0]}`)} className="font-semibold hover:underline text-left">{(s.dog_names || []).join(", ") || "—"}</button>
                    <span className="text-muted-foreground"> · {s.owner_name}</span>
                    <div className="text-sm text-muted-foreground">{s.service_type} · {fmtDate(s.date)}{s.start_time ? ` · ${s.start_time}` : ""} · <span data-testid={`dv-duration-${s.id}`}>{fmtDuration(s.duration_minutes) || "—"}</span></div>
                  </div>
                  <StatusBadge value={s.status} />
                </div>
                <div className="mt-3 flex gap-2 flex-wrap">
                  <Button size="sm" data-testid={`dv-complete-${s.id}`} onClick={() => setComplete(s)} className="rounded-full"><CheckCircle2 className="h-4 w-4 mr-1" />Svolta</Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" variant="outline" data-testid={`dv-cancel-${s.id}`} className="rounded-full"><XCircle className="h-4 w-4 mr-1" />Annulla</Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Annullare questa prestazione?</AlertDialogTitle>
                        <AlertDialogDescription asChild>
                          <div>
                            <span className="block">Cane: <b>{(s.dog_names || []).join(", ") || "—"}</b></span>
                            <span className="block">Servizio: <b>{s.service_type}</b></span>
                            <span className="block">Data: <b>{fmtDate(s.date)}{s.start_time ? ` · ${s.start_time}` : ""}</b></span>
                            <span className="block mt-2">La prestazione resterà nello storico con stato <b>Annullata</b> e non consumerà crediti del pacchetto.</span>
                          </div>
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Indietro</AlertDialogCancel>
                        <AlertDialogAction data-testid={`dv-cancel-confirm-${s.id}`} onClick={() => cancelSession(s)}>Conferma annullamento</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
        </CollapsibleSection>
      )}

      {/* Contatti da gestire */}
      {contatti.length > 0 && (
        <CollapsibleSection id="dash-contatti" testid="contatti-section" title="Contatti da gestire" icon={UserCog} iconClass="text-accent"
          badge={<span data-testid="contatti-count" className="text-sm font-bold px-2 py-0.5 rounded-full bg-[#F3E5F5] text-[#6A1B9A] border border-[#CE93D8]">{contatti.length}</span>}>
          <div className="space-y-2" data-testid="contatti-list">
            {contatti.map((c) => (
              <div key={c.owner_id} className="rounded-2xl border border-border bg-card p-4" data-testid={`contatto-${c.owner_id}`}>
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <button onClick={() => nav(`/clienti/${c.owner_id}`)} className="font-semibold hover:underline text-left">{c.referente || "—"}</button>
                    <div className="text-sm text-muted-foreground">
                      {(c.dog_names || []).join(", ") || "—"}
                      {c.last_service ? ` · ${c.last_service}` : ""}{c.last_date ? ` · ${fmtDate(c.last_date)}` : ""}
                      {c.telefono ? <span className="inline-flex items-center gap-1 ml-1"><Phone className="h-3 w-3" />{c.telefono}</span> : null}
                    </div>
                    {c.follow_up_date || c.follow_up_note ? (
                      <div className="text-xs font-semibold text-accent mt-0.5" data-testid={`contatto-followup-${c.owner_id}`}>
                        Follow-up{c.follow_up_date ? `: ${fmtDate(c.follow_up_date)}` : ""}{c.follow_up_note ? ` · ${c.follow_up_note}` : ""}
                      </div>
                    ) : null}
                  </div>
                  <ContactBadge value={c.stage} />
                </div>
                <div className="mt-3 flex gap-2 flex-wrap">
                  <Button size="sm" className="rounded-full" data-testid={`contatto-convert-${c.owner_id}`} onClick={() => nav(`/clienti/${c.owner_id}`)}>Converti in cliente</Button>
                  <Button size="sm" variant="outline" className="rounded-full" data-testid={`contatto-occasionale-${c.owner_id}`} onClick={() => setStage(c.owner_id, "Occasionale")}>Occasionale</Button>
                  <Button size="sm" variant="outline" className="rounded-full" data-testid={`contatto-chiudi-${c.owner_id}`} onClick={() => setStage(c.owner_id, "Chiuso")}>Chiudi</Button>
                </div>
              </div>
            ))}
          </div>
        </CollapsibleSection>
      )}

      {/* Oggi */}
      <CollapsibleSection id="dash-oggi" testid="dash-oggi" title="Oggi" icon={CalendarClock} iconClass="text-primary">
        <div className="space-y-2" data-testid="today-list">
          {(data?.today || []).length === 0 && (
            <div className="rounded-2xl border border-dashed border-border p-6 text-center text-muted-foreground text-sm">Nessuna attività pianificata per oggi.</div>
          )}
          {(data?.today || []).map((s) => (
            <div key={s.id} className="rounded-2xl border border-border bg-card p-4">
              <div className="flex items-start gap-3">
                <div className="font-mono text-sm text-muted-foreground w-14 shrink-0">{s.start_time || "--:--"}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold break-words">{(s.dog_names || []).join(", ") || "—"} <span className="text-muted-foreground font-normal">· {s.owner_name}</span></div>
                  <div className="text-sm text-muted-foreground">{s.service_type} · <span data-testid={`today-duration-${s.id}`}>{fmtDuration(s.duration_minutes) || "—"}</span></div>
                </div>
                <div className="hidden sm:flex items-center gap-2 shrink-0">
                  <StatusBadge value={s.status} />
                  {s.status === "Pianificata" && (
                    <Button size="sm" data-testid={`today-complete-${s.id}`} onClick={() => setComplete(s)} className="rounded-full">
                      <CheckCircle2 className="h-4 w-4 mr-1" /> Svolta
                    </Button>
                  )}
                </div>
              </div>
              <div className="mt-2 flex items-center justify-between gap-2 sm:hidden">
                <StatusBadge value={s.status} />
                {s.status === "Pianificata" && (
                  <Button size="sm" data-testid={`today-complete-m-${s.id}`} onClick={() => setComplete(s)} className="rounded-full">
                    <CheckCircle2 className="h-4 w-4 mr-1" /> Svolta
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </CollapsibleSection>

      {/* Da controllare */}
      <CollapsibleSection id="dash-controllare" testid="dash-controllare" title="Da controllare" icon={AlertCircle} iconClass="text-accent">
        <div className="grid lg:grid-cols-2 gap-3">
          <div className="rounded-2xl border border-border bg-card p-4">
            <div className="text-sm font-bold mb-2 flex items-center gap-2"><PackageX className="h-4 w-4" /> Pacchetti</div>
            {att.packages.length === 0 && <p className="text-sm text-muted-foreground">Tutto in regola.</p>}
            <div className="space-y-2">
              {att.packages.map((p) => (
                <button key={p.id} onClick={() => nav(`/cani/${p.dog_id}`)} className="w-full text-left rounded-xl border border-border p-2.5 hover:bg-secondary">
                  <div className="flex items-center justify-between"><span className="font-semibold text-sm">{p.dog_name}</span><StatusBadge value={p.status} /></div>
                  <div className="text-xs text-muted-foreground">{p.remaining_credits} crediti residui</div>
                </button>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-border bg-card p-4">
            <div className="text-sm font-bold mb-2 flex items-center gap-2"><Wallet className="h-4 w-4" /> Finanze</div>
            {att.finance.length === 0 && <p className="text-sm text-muted-foreground">Nessun insoluto.</p>}
            <div className="space-y-2">
              {att.finance.map((p) => (
                <button key={p.id} onClick={() => nav(`/cani/${p.dog_id}`)} className="w-full text-left rounded-xl border border-border p-2.5 hover:bg-secondary">
                  <div className="flex items-center justify-between"><span className="font-semibold text-sm">{p.dog_name}</span><StatusBadge value={p.payment_status} /></div>
                  <div className="text-xs text-muted-foreground">Da incassare {fmtEUR(p.outstanding)}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* Senza prossimo appuntamento */}
      <CollapsibleSection id="dash-workflow" testid="dash-workflow" title="Senza prossimo appuntamento" icon={CalendarClock} iconClass="text-primary">
        <div className="rounded-2xl border border-border bg-card p-4">
          {att.workflow.length === 0 && <p className="text-sm text-muted-foreground">Nulla da pianificare.</p>}
          <div className="space-y-2">
            {att.workflow.map((w) => (
              <button key={w.dog_id} onClick={() => nav(`/cani/${w.dog_id}`)} className="w-full text-left rounded-xl border border-border p-2.5 hover:bg-secondary">
                <div className="font-semibold text-sm">{w.dog_name}</div>
                <div className="text-xs text-muted-foreground">{w.owner_name}</div>
              </button>
            ))}
          </div>
        </div>
      </CollapsibleSection>

      <CompleteDialog open={!!complete} onOpenChange={(v) => !v && setComplete(null)} session={complete} onDone={load} />
    </div>
  );
}
